import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { drawOnboardingArt, listOnboardingConfigs } from '@platform/client';
import {
  onboardingArtCandidateSchema,
  onboardingConfigsSchema,
} from '@platform/schema/onboarding';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';

import type { DrawOnboardingArtData } from '@platform/client';
import type {
  OnboardingArtCandidate,
  StepArt,
} from '@platform/schema/onboarding';

type DrawOnboardingArt = DrawOnboardingArtData['body'];

const PREVIEW = 200;
const THUMBNAIL = 96;
/** The read-aloud description's own ceiling in the step art schema. */
const DESCRIPTION_MAX = 300;

function Visual({
  art,
  label,
  width,
}: {
  art: StepArt | undefined;
  label: string;
  width: number;
}) {
  return (
    <Stack spacing={0.5} sx={{ width }}>
      {art?.kind === 'IMAGE' ? (
        <img
          src={art.uri}
          alt={art.description ?? ''}
          width={width}
          style={{ objectFit: art.fit }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {art === undefined ? 'The screen’s own (LEO)' : 'No illustration'}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

/**
 * The visuals this screen had in earlier config versions, newest first, so a visual
 * replaced and published can be put back in one click. Read from the versions the
 * console already lists: nothing else stores them.
 */
function Previous({
  stepId,
  art,
  disabled,
  onApply,
}: {
  stepId: string;
  art: StepArt | undefined;
  disabled: boolean;
  onApply: (art: StepArt | undefined) => void;
}) {
  const configs = useQuery({
    queryKey: ['onboardingConfigs'],
    queryFn: async () => {
      const { data } = await listOnboardingConfigs({ throwOnError: true });
      return onboardingConfigsSchema.parse(data);
    },
  });
  const seen = new Set([JSON.stringify(art ?? null)]);
  const earlier = (configs.data?.versions ?? []).flatMap(
    ({ version, config }) => {
      const had = config.flow.find((step) => step.id === stepId)?.art;
      const key = JSON.stringify(had ?? null);
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ version, art: had }];
    },
  );
  if (earlier.length === 0) return null;

  return (
    <Stack spacing={1}>
      <Typography variant="body2">Previous visuals</Typography>
      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {earlier.map((one) => (
          <Stack key={one.version} spacing={0.5}>
            <Visual
              art={one.art}
              label={`Version ${String(one.version)}`}
              width={THUMBNAIL}
            />
            <Button
              size="small"
              disabled={disabled}
              onClick={() => {
                onApply(one.art);
              }}
            >
              Use this
            </Button>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * Ask the AI for a visual for this screen, see it beside the current one, and apply it
 * to the draft or discard it — ONB-21. Applying is an edit like any other: learners see
 * it once the draft is published.
 */
export function ArtGenerate({
  stepId,
  screen,
  art,
  disabled,
  onApply,
}: {
  stepId: string;
  /** What the screen is for, which the drawing has to suit. */
  screen: string;
  art: StepArt | undefined;
  disabled: boolean;
  onApply: (art: StepArt | undefined) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [feedback, setFeedback] = useState('');
  const [candidate, setCandidate] = useState<OnboardingArtCandidate>();

  const draw = useTrackedMutation('Generate a visual', {
    mutationFn: async (body: DrawOnboardingArt) =>
      onboardingArtCandidateSchema.parse(
        (await drawOnboardingArt({ body, throwOnError: true })).data,
      ),
    onSuccess: (next) => {
      setCandidate(next);
      setFeedback('');
    },
  });

  const busy = disabled || draw.isPending;
  const proposed: StepArt | undefined =
    candidate?.uri == null
      ? undefined
      : {
          kind: 'IMAGE',
          uri: candidate.uri,
          size: art?.kind === 'IMAGE' ? art.size : 'medium',
          fit: 'contain',
          decorative: false,
          description: prompt.trim().slice(0, DESCRIPTION_MAX),
        };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">Generate with AI</Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          label="Describe the visual"
          size="small"
          multiline
          placeholder="A door opening onto a winding path"
          value={prompt}
          disabled={disabled}
          onChange={(event) => {
            setPrompt(event.target.value);
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={busy || prompt.trim().length === 0}
          onClick={() => {
            draw.mutate({ screen, prompt });
          }}
        >
          Generate
        </Button>
      </Stack>
      {draw.isPending && (
        <Typography variant="body2">
          Drawing… this takes a minute or two.
        </Typography>
      )}
      {draw.isError && (
        <Alert severity="error">
          {draw.error.message} The current visual is kept.
        </Alert>
      )}
      {candidate?.refusal != null && (
        <Alert severity="error">
          {candidate.refusal} The current visual is kept.
        </Alert>
      )}

      {proposed !== undefined && candidate?.drawing != null && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={2}>
            <Visual art={art} label="Current" width={PREVIEW} />
            <Visual art={proposed} label="Proposed" width={PREVIEW} />
          </Stack>
          {candidate.warning !== null && (
            <Alert severity="warning">{candidate.warning}</Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
              label="What to change"
              size="small"
              value={feedback}
              disabled={disabled}
              onChange={(event) => {
                setFeedback(event.target.value);
              }}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              disabled={busy || feedback.trim().length === 0}
              onClick={() => {
                draw.mutate({
                  screen,
                  prompt,
                  refine: { drawing: candidate.drawing ?? '', feedback },
                });
              }}
            >
              Refine
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              disabled={busy}
              onClick={() => {
                onApply(proposed);
                setCandidate(undefined);
              }}
            >
              Apply to draft
            </Button>
            <Button
              size="small"
              disabled={draw.isPending}
              onClick={() => {
                setCandidate(undefined);
              }}
            >
              Discard
            </Button>
          </Stack>
        </Stack>
      )}

      <Previous
        stepId={stepId}
        art={art}
        disabled={disabled}
        onApply={onApply}
      />
    </Stack>
  );
}
